import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import { createProject, fixArea, getProjects } from "../../api/FlaskAPI";
import { getErrorMessage } from "../../api/errors";
import { setProjects } from "../../reducers/AuthSlice";
import AreaInputMethod from "../../components/AreaInputMethod";
import { useTranslation } from "react-i18next";

export default function AddProjectPanel({
  token,
  areas,
  onAreasChange,
  boundaryMethod,
  onBoundaryMethodChange,
  onProjectCreated,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [projectName, setProjectName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isFixing, setIsFixing] = useState(false);
  const [highlightInput, setHighlightInput] = useState(1);

  const handleClearAreas = () => {
    onAreasChange(null);
  };

  const handleShapeLoaded = (shape) => {
    if (!shape) {
      handleClearAreas();
      return;
    }
    onAreasChange({
      id: shape.polyId,
      source: "upload",
      valid: shape.is_area_valid,
      geojson: shape.geojson,
    });
  };

  const handleCoordinatesApplied = (shape) => {
    if (!shape) {
      handleClearAreas();
      return;
    }
    onAreasChange({
      id: shape.polyId,
      source: "coordinates",
      valid: shape.is_area_valid,
      geojson: {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "Polygon",
              coordinates: [shape.coordinates],
            },
            properties: {},
          },
        ],
      },
    });
  };

  const handleFixArea = async () => {
    if (!areas || areas.source !== "coordinates" || !areas.geojson) return;
    setIsFixing(true);
    try {
      const coordinates = areas.geojson.features[0].geometry.coordinates[0];
      const response = await fixArea({ coordinates, crs: "EPSG:4326" });
      if (response.status === 200 && response.data.coordinates) {
        onAreasChange({
          ...areas,
          id: `uploaded-${Date.now()}`,
          valid: response.data.is_area_valid,
          geojson: {
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: {
                  type: "Polygon",
                  coordinates: [response.data.coordinates],
                },
                properties: {},
              },
            ],
          },
        });
        toast.success(t("area.toasts.fixed"));
      } else {
        toast.error(t("area.toasts.fixAuto"));
      }
    } catch (error) {
      toast.error(getErrorMessage(error, t("area.toasts.fixFailed")));
    } finally {
      setIsFixing(false);
    }
  };

  const handleCancel = () => {
    navigate("/");
  };

  const handleSubmit = async () => {
    if (!projectName.trim()) {
      toast.error(t("area.toasts.projectName"));
      return;
    }
    // In draw mode `areas` exists even with nothing drawn, so count shapes.
    if (!areas?.geojson?.features?.length) {
      toast.error(t("area.toasts.addAreaFirst"));
      setHighlightInput((n) => n + 1);
      return;
    }
    if (!areas.valid) {
      toast.error(t("area.toasts.areaNotValid"));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: projectName.trim(),
        geojson: areas.geojson,
        token,
      };
      const response = await createProject(payload);
      if (response.status === 200 || response.status === 201) {
        toast.success(t("area.toasts.projectCreated"));
        onProjectCreated();
        navigate("/");
        try {
          const projectsResponse = await getProjects();
          if (projectsResponse && projectsResponse.data) {
            dispatch(setProjects(projectsResponse.data));
          }
        } catch (projectsErr) {
          console.error("Error fetching projects:", projectsErr);
        }
      } else {
        toast.error(response?.data?.message || t("area.toasts.projectFailed"));
      }
    } catch (error) {
      toast.error(getErrorMessage(error, t("area.toasts.projectFailed")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      className="glass-panel"
      sx={{
        width: "350px",
        maxHeight: "calc(100vh - 40px)",
        overflowY: "auto",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, color: "primary.main" }}
      >
        {t("area.titles.addProject")}
      </Typography>

      <AreaInputMethod
        componentSource={"project"}
        boundaryMethod={boundaryMethod}
        onBoundaryMethodChange={onBoundaryMethodChange}
        onClear={handleClearAreas}
        onShapeLoaded={handleShapeLoaded}
        onCoordinatesApplied={handleCoordinatesApplied}
        highlight={highlightInput}
      />

      {areas?.source === "coordinates" && areas?.valid === false && (
        <Box
          sx={{
            p: 1.5,
            border: "1px solid",
            borderColor: "error.main",
            borderRadius: "10px",
            bgcolor: "error.light",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "error.dark", fontWeight: 600 }}
          >
            {t("area.invalid")}
          </Typography>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={handleFixArea}
            disabled={isFixing}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
            }}
          >
            {isFixing ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              t("area.fix")
            )}
          </Button>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: "primary.main" }}
        >
          {t("area.metadata")}
        </Typography>
        <TextField
          label={t("area.projectName")}
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          fullWidth
          size="small"
          required
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1, mt: "auto" }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleCancel}
          fullWidth
          disabled={submitting}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
          }}
        >
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          fullWidth
          disabled={
            submitting ||
            !projectName.trim() ||
            !areas ||
            areas?.valid === false
          }
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "10px",
          }}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("area.submit")
          )}
        </Button>
      </Box>
    </Box>
  );
}
